import React, { useEffect, useState } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useTranslation } from 'react-i18next';

interface SpotlightTourProps {
    activeTab: string;
}

// Clé de stockage pour savoir ce qui a déjà été vu
const TOUR_STORAGE_KEY = 'padelo_tour_seen';

// Versions des tours par onglet. Incrémenter pour forcer le tour pour tous les utilisateurs.
const TOUR_VERSIONS: Record<string, number> = {
    'ranking': 1,
    'matches': 1,
    'clubs': 1,
    'history': 1,
    'players': 2, // Version 2 : Intégration auto-expansion et recherche FFT
};

export default function SpotlightTour({ activeTab }: SpotlightTourProps) {
    const { t } = useTranslation();
    const [run, setRun] = useState(false);
    const [steps, setSteps] = useState<Step[]>([]);
    // Placeholder for theme if needed, or hardcoded PadELO green
    const primaryColor = '#10b981'; // padel-green

    useEffect(() => {
        // Dispatch custom event to let other components know the tour is running
        window.dispatchEvent(new CustomEvent('padelo-tour-status', { detail: { isRunning: run, tab: activeTab } }));
    }, [run, activeTab]);

    useEffect(() => {
        // Petit délai pour laisser le temps au DOM de la tab de se charger
        const timer = setTimeout(() => {
            checkAndRunTour(activeTab);
        }, 500);

        return () => clearTimeout(timer);
    }, [activeTab, t]);

    const checkAndRunTour = (tab: string) => {
        // If the notification permission popup is currently visible, defer the tour
        if (document.getElementById('notification-permission-modal')) {
            setTimeout(() => checkAndRunTour(tab), 500);
            return;
        }

        // Only valid tabs
        if (!['ranking', 'matches', 'clubs', 'history', 'players'].includes(tab)) {
            setRun(false);
            return;
        }

        try {
            const stored = localStorage.getItem(TOUR_STORAGE_KEY);
            const seenTours = stored ? JSON.parse(stored) : {};

            const currentVersion = TOUR_VERSIONS[tab] || 1;
            const savedVersion = seenTours[tab];

            // Si le tour pour cette tab a déjà été vu dans cette version (ou plus récente), on ne fait rien
            if (savedVersion && typeof savedVersion === 'number' && savedVersion >= currentVersion) {
                setRun(false);
                return;
            }
            
            // Compatibilité avec l'ancien système (boolean)
            if (savedVersion === true && currentVersion === 1) {
                setRun(false);
                return;
            }

            // Configuration des étapes selon l'onglet
            const tabSteps = getStepsForTab(tab, t);

            if (tabSteps.length > 0) {
                setSteps(tabSteps);
                setRun(true);
            } else {
                setRun(false);
            }
        } catch (e) {
            console.error("Erreur lecture storage tour", e);
            setRun(false);
        }
    };

    const getStepsForTab = (tab: string, t: any): Step[] => {
        switch (tab) {
            case 'ranking':
                return [
                    {
                        target: '#tour-ranking-scope',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.ranking.scope.title')}</h3>
                                <p className="text-sm">{t('tour.ranking.scope.desc')}</p>
                            </div>
                        ),
                        disableBeacon: true,
                        placement: 'bottom',
                    },
                    {
                        target: '#tour-ranking-filters',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.ranking.filters.title')}</h3>
                                <p className="text-sm">{t('tour.ranking.filters.desc')}</p>
                            </div>
                        ),
                        placement: 'bottom',
                    },
                    {
                        target: '#tour-ranking-list',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.ranking.details.title')}</h3>
                                <p className="text-sm">{t('tour.ranking.details.desc')}</p>
                            </div>
                        ),
                        placement: 'top',
                    }
                ];
            case 'matches':
                return [
                    {
                        target: '#tour-matches-create',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.matches.create.title')}</h3>
                                <p className="text-sm">{t('tour.matches.create.desc')}</p>
                            </div>
                        ),
                        disableBeacon: true,
                        placement: 'bottom',
                    },
                    {
                        target: '#tour-matches-list',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.matches.validation.title')}</h3>
                                <p className="text-sm" dangerouslySetInnerHTML={{ __html: t('tour.matches.validation.desc') }} />
                            </div>
                        ),
                        placement: 'top',
                    }
                ];
            case 'clubs':
                return [
                    {
                        target: '#tour-clubs-create',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.clubs.create.title')}</h3>
                                <p className="text-sm">{t('tour.clubs.create.desc')}</p>
                            </div>
                        ),
                        disableBeacon: true,
                        placement: 'bottom',
                    },
                    {
                        target: '#tour-clubs-podium',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.clubs.podium.title')}</h3>
                                <p className="text-sm">{t('tour.clubs.podium.desc')}</p>
                            </div>
                        ),
                        placement: 'bottom',
                    }
                ];
            case 'players':
                return [
                    {
                        target: '#tour-players-search',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.players.search.title')}</h3>
                                <p className="text-sm">{t('tour.players.search.desc')}</p>
                            </div>
                        ),
                        disableBeacon: true,
                        placement: 'bottom',
                    },
                    {
                        target: '#tour-players-filters',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.players.filters.title')}</h3>
                                <p className="text-sm">{t('tour.players.filters.desc')}</p>
                            </div>
                        ),
                        placement: 'bottom',
                    },
                    {
                        target: '#tour-players-card',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.players.favorite.title')}</h3>
                                <p className="text-sm">{t('tour.players.favorite.desc')}</p>
                            </div>
                        ),
                        placement: 'bottom',
                    }
                ];
            case 'history':
                return [
                    {
                        target: '#tour-history-list',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.history.performances.title')}</h3>
                                <p className="text-sm">{t('tour.history.performances.desc')}</p>
                            </div>
                        ),
                        disableBeacon: true,
                        placement: 'top',
                    },
                    {
                        target: '#tour-history-search',
                        content: (
                            <div>
                                <h3 className="font-bold text-lg mb-2">{t('tour.history.search.title')}</h3>
                                <p className="text-sm">{t('tour.history.search.desc')}</p>
                            </div>
                        ),
                        placement: 'bottom',
                    }
                ];
            default:
                return [];
        }
    };

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, type, index } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        // Émettre l'index de l'étape actuelle
        if (type === 'step:after' || type === 'tour:start' || type === 'step:before') {
            window.dispatchEvent(new CustomEvent('padelo-tour-step', { detail: { index, type, tab: activeTab } }));
        }

        if (finishedStatuses.includes(status)) {
            setRun(false);
            // Marquer ce tour comme vu avec sa version
            try {
                const stored = localStorage.getItem(TOUR_STORAGE_KEY);
                const seenTours = stored ? JSON.parse(stored) : {};
                seenTours[activeTab] = TOUR_VERSIONS[activeTab] || 1;
                localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(seenTours));
            } catch (e) {
                console.error("Erreur sauvegarde tour", e);
            }
        }
    };

    // Exposée globalement (via window ou un event eventuel) pour pouvoir re-déclencher un tour manuellement
    // On attache une fonction à l'objet window pour la simplicité, bien qu'un context serait plus "React"
    // Mais utile pour déclencher depuis d'autres endroits facilement sans refactorer l'arbre complet
    useEffect(() => {
        (window as any).restartPadeloTour = (tab: string) => {
            try {
                const stored = localStorage.getItem(TOUR_STORAGE_KEY);
                const seenTours = stored ? JSON.parse(stored) : {};
                seenTours[tab] = false;
                localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(seenTours));

                // Si on est déjà sur l'onglet, on force le redémarrage
                if (activeTab === tab) {
                    setRun(false);
                    setTimeout(() => {
                        checkAndRunTour(tab);
                    }, 100);
                }
            } catch (e) {
                console.error(e);
            }
        };

        return () => {
            delete (window as any).restartPadeloTour;
        };
    }, [activeTab]);

    return (
        <Joyride
            callback={handleJoyrideCallback}
            continuous
            disableScrollParentFix={true}
            hideCloseButton
            run={run}
            scrollToFirstStep
            showProgress
            showSkipButton
            steps={steps}
            locale={{
                back: t('onboarding.buttons.previous'),
                close: t('common.close'),
                last: t('onboarding.buttons.finish'),
                next: t('onboarding.buttons.next'),
                skip: t('onboarding.buttons.skip'),
            }}
            styles={{
                options: {
                    zIndex: 10000,
                    primaryColor: primaryColor,
                    textColor: '#333',
                },
                buttonNext: {
                    backgroundColor: primaryColor,
                },
                buttonBack: {
                    marginRight: 10,
                    color: '#666',
                },
                buttonSkip: {
                    color: '#999',
                }
            }}
        />
    );
}
